import { useState } from "react";
import { motion } from "framer-motion";
import { HandHeart, Leaf, Users, ArrowUpRight, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Reveal } from "./Reveal";
import { RESEAU } from "../../data/content";

const ICONS = { hebergeur: HandHeart, "bien-etre": Leaf, relais: Users };

const PartnerDialog = ({ role, open, onOpenChange }) => {
  const [form, setForm] = useState({
    nom: "",
    structure: "",
    email: "",
    tel: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);

  if (!role) return null;
  const Icon = ICONS[role.key];

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/partenaires", {
        role: role.title,
        nom: form.nom,
        structure: form.structure,
        email: form.email,
        tel: form.tel,
        message: form.message,
      });
      toast.success("Demande envoyée", {
        description: "Merci ! L'association vous recontactera très vite.",
      });
      onOpenChange(false);
    } catch (err) {
      toast.error("Envoi impossible", {
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="partner-dialog"
        className="max-h-[90vh] overflow-y-auto rounded-3xl border-pblv-ink/10 bg-pblv-bg sm:max-w-lg"
      >
        <DialogHeader>
          <span className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-pblv-mauve/12 text-pblv-mauve">
            <Icon className="h-6 w-6" strokeWidth={1.6} />
          </span>
          <DialogTitle className="font-serif text-3xl font-medium text-pblv-ink">
            {role.title}
          </DialogTitle>
          <DialogDescription className="text-pblv-inksoft">
            {role.body}
          </DialogDescription>
        </DialogHeader>

        <ul className="my-2 flex flex-col gap-2 rounded-2xl bg-white p-4 text-sm text-pblv-ink">
          {role.details.map((d) => (
            <li key={d} className="flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-pblv-sage" />
              {d}
            </li>
          ))}
        </ul>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-nom">Nom & prénom</Label>
              <Input
                id="p-nom"
                data-testid="partner-input-nom"
                required
                value={form.nom}
                onChange={update("nom")}
                placeholder="Votre nom"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-structure">Structure / lieu</Label>
              <Input
                id="p-structure"
                data-testid="partner-input-structure"
                value={form.structure}
                onChange={update("structure")}
                placeholder="Nom de l'établissement"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-email">E-mail</Label>
              <Input
                id="p-email"
                type="email"
                data-testid="partner-input-email"
                required
                value={form.email}
                onChange={update("email")}
                placeholder="vous@exemple.com"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-tel">Téléphone</Label>
              <Input
                id="p-tel"
                data-testid="partner-input-tel"
                value={form.tel}
                onChange={update("tel")}
                placeholder="0690 ..."
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="p-message">Votre message</Label>
            <Textarea
              id="p-message"
              data-testid="partner-input-message"
              rows={3}
              value={form.message}
              onChange={update("message")}
              placeholder="Présentez votre projet, votre lieu ou vos prestations…"
            />
          </div>
          <button
            type="submit"
            data-testid="partner-submit"
            disabled={submitting}
            className="group mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-pblv-mauve px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-pblv-mauvedark disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Send className="h-4 w-4" strokeWidth={1.8} />
            {submitting ? "Envoi en cours…" : "Envoyer ma demande d'inscription"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const Reseau = () => {
  const [openKey, setOpenKey] = useState(null);
  const activeRole = RESEAU.find((r) => r.key === openKey) || null;

  return (
    <section
      id="reseau"
      data-testid="reseau-section"
      className="relative bg-pblv-bg py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-8">
        <div className="mb-16 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.25em] text-pblv-mauve">
                Rejoindre le réseau
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl font-medium leading-[1.05] tracking-tight text-pblv-ink md:text-6xl">
                Un territoire solidaire
                <br />
                <span className="italic">se construit ensemble.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.2}>
              <p className="text-lg leading-relaxed text-pblv-inksoft">
                Hébergeurs, professionnels du bien-être, relais associatifs :
                chacun a un rôle à jouer. Cliquez sur une fiche pour vous
                inscrire.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {RESEAU.map((r, i) => {
            const Icon = ICONS[r.key];
            return (
              <Reveal key={r.key} delay={i * 0.1} y={36}>
                <motion.button
                  type="button"
                  onClick={() => setOpenKey(r.key)}
                  data-testid={`reseau-card-${r.key}`}
                  whileHover={{ y: -8 }}
                  transition={{ type: "spring", stiffness: 240, damping: 20 }}
                  className="group flex h-full w-full flex-col rounded-[1.75rem] border border-pblv-ink/10 bg-pblv-bg2 p-8 text-left transition-colors duration-500 hover:border-pblv-mauve/30 hover:bg-white"
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-pblv-mauve/12 text-pblv-mauve transition-colors duration-500 group-hover:bg-pblv-mauve group-hover:text-white">
                      <Icon className="h-6 w-6" strokeWidth={1.6} />
                    </span>
                    <ArrowUpRight className="h-5 w-5 text-pblv-inksoft transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-pblv-mauve" />
                  </div>
                  <h3 className="mt-6 font-serif text-2xl font-medium text-pblv-ink">
                    {r.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-pblv-inksoft">
                    {r.body}
                  </p>
                  <span className="mt-6 text-sm font-semibold text-pblv-mauve">
                    S'inscrire →
                  </span>
                </motion.button>
              </Reveal>
            );
          })}
        </div>
      </div>

      <PartnerDialog
        key={openKey || "none"}
        role={activeRole}
        open={!!activeRole}
        onOpenChange={(v) => !v && setOpenKey(null)}
      />
    </section>
  );
};

export default Reseau;
